import type { JSONContent } from '@tiptap/react';

import type {
  EditorPageContent,
  EditorPageRef,
  EditorPageSubtreeNode,
  PageProvider,
} from '@/components/ui/block-editor';
import { pagesApi } from './api';
import type { WikiPage } from './types';

function toContent(p: WikiPage): EditorPageContent {
  return {
    id: p.id,
    public_id: p.public_id,
    title: p.title,
    icon: p.icon,
    body: p.body,
    body_json: (p.body_json as unknown as JSONContent) ?? null,
  };
}

/** Bridge a project's Pages API to the block editor's page-embed (FE-ADR-055).
 *  Search is scoped to the current project; `open` focuses a page in the Pages
 *  tab. `selfPublicId` is the page being edited — excluded from the picker so a
 *  page can't embed a link to itself. Everything routes through the
 *  org/project-scoped API, so an embed can't surface a page outside access. */
export function makeProjectPageProvider(
  projectId: number,
  open: (publicId: string) => void,
  selfPublicId?: string,
): PageProvider {
  return {
    async search(query: string): Promise<EditorPageRef[]> {
      const { results } = await pagesApi.list({ project: projectId, search: query, is_archived: false });
      return results
        .filter((p) => p.public_id !== selfPublicId)
        .map((p) => ({ id: p.id, public_id: p.public_id, title: p.title, icon: p.icon }));
    },
    async resolve(publicId: string): Promise<EditorPageContent> {
      return toContent(await pagesApi.get(publicId));
    },
    async subtree(publicId: string): Promise<{ pages: EditorPageSubtreeNode[]; truncated: boolean }> {
      const { pages, truncated } = await pagesApi.subtree(publicId);
      return { pages: pages.map((p) => ({ ...toContent(p), depth: p.depth })), truncated };
    },
    open,
    hostPageId: selfPublicId ?? null,
  };
}
